import { z } from 'zod';

import { parseWallClock, todayWallClockDate, toWallClockDate } from '@/utils';

// TOG_scheduleBase description and comment are nvarchar(100).
export const SO_SCHEDULE_TEXT_MAX_LENGTH = 100;

const lookupRefSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
});

// Messages are i18n keys, translated where the errors are rendered.
export const editSoScheduleSchema = z.object({
  donor: lookupRefSchema.nullable().refine((value) => !!value?.id, {
    message: 'donorScheduling.editSoSchedule.requiredDonor',
  }),
  scheduleMaster: lookupRefSchema.nullable().refine((value) => !!value?.id, {
    message: 'donorScheduling.editSoSchedule.requiredScheduleMaster',
  }),
  description: z
    .string()
    .trim()
    .min(1, 'donorScheduling.editSoSchedule.requiredDescription')
    .max(
      SO_SCHEDULE_TEXT_MAX_LENGTH,
      'donorScheduling.editSoSchedule.descriptionTooLong',
    ),
  targetDate: z
    .date()
    .nullable()
    // Explicit boolean return: an inferred type predicate would narrow the form type to Date.
    .refine((value): boolean => value != null, {
      message: 'donorScheduling.editSoSchedule.requiredTargetDate',
    }),
  targetTime: z.boolean().nullable(),
  site: lookupRefSchema.nullable(),
  fresh: z.number().nullable(),
  fshDrug1: lookupRefSchema.nullable(),
  fshDrug2: lookupRefSchema.nullable(),
  comment: z
    .string()
    .trim()
    .max(
      SO_SCHEDULE_TEXT_MAX_LENGTH,
      'donorScheduling.editSoSchedule.commentTooLong',
    ),
  clinicRanchHaulIn: z.number().nullable(),
  billDrugs: z.boolean().nullable(),
  drugShipFee: z.number().nullable(),
  fshNext: z.number().nullable(),
  owner: lookupRefSchema.nullable(),
  billStatus: z.number().nullable(),
});

export type EditSoScheduleFormData = z.infer<typeof editSoScheduleSchema>;

/**
 * The SO schedule locks once the donor schedule's day has passed (ADO 36879).
 * Compares local calendar dates, so 8pm on the schedule's day is still editable.
 * TODO(36879): admins will be able to edit past-date SO schedules.
 */
export function isSoScheduleLocked(
  donorScheduleDate: string | null | undefined,
  today: string = todayWallClockDate(),
): boolean {
  const date = parseWallClock(donorScheduleDate);
  return date != null && toWallClockDate(date) < today;
}

export function getEditableSoScheduleId(
  selected:
    | {
        date?: string | null;
        soSchedule?: { id?: string | null } | null;
      }[]
    | null
    | undefined,
  today: string = todayWallClockDate(),
): string | undefined {
  if (selected?.length !== 1) return undefined;
  const [donorSchedule] = selected;
  if (isSoScheduleLocked(donorSchedule.date, today)) return undefined;
  return donorSchedule.soSchedule?.id ?? undefined;
}
